import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Shield, Scale, CheckCircle, ChevronRight,
  Loader2, Building2, GraduationCap, Users, Camera, FileText as DocIcon, List
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { useQueryClient } from '@tanstack/react-query';
import { useGrantMultipleConsents } from '@/hooks/useConsents';
import DocxViewer from '@/components/DocxViewer';
import logo from '/media/Blue_Logo_Animated.mp4';
import background from '/media/background.png';

// Document configuration with storage paths
const DOCUMENTS = {
  terms: {
    title: 'Regulamin',
    icon: FileText,
    storagePath: 'Legal/Regulamin_KConnect_v1.docx',
    description: 'Regulamin korzystania z platformy KConnect określający zasady użytkowania, prawa i obowiązki użytkowników oraz warunki świadczenia usług.',
    summary: `Główne postanowienia Regulaminu:

• Zasady korzystania z Platformy KConnect
• Prawa i obowiązki użytkowników
• Ochrona danych logowania
• Odpowiedzialność za treści
• Warunki świadczenia usług
• Postanowienia końcowe i zmiany regulaminu`
  },
  dpa: {
    title: 'Umowa Powierzenia Przetwarzania Danych (DPA)',
    icon: Scale,
    storagePath: 'Legal/DPA_KConnect_v1.docx',
    description: 'Umowa powierzenia przetwarzania danych osobowych określająca zakres i zasady przetwarzania danych przez KConnect.',
    summary: `Główne postanowienia Umowy DPA:

• Zakres powierzonych danych osobowych
• Cele i podstawy przetwarzania
• Obowiązki podmiotu przetwarzającego
• Środki bezpieczeństwa danych
• Prawa do audytu
• Procedury w przypadku naruszeń`
  },
  privacy: {
    title: 'Polityka Prywatności',
    icon: Shield,
    storagePath: 'Legal/Polityka_Prywatnosci_KConnect_v1.docx',
    description: 'Polityka prywatności opisująca sposób zbierania, przetwarzania i ochrony danych osobowych użytkowników platformy.',
    summary: `Główne postanowienia Polityki Prywatności:

• Informacje o administratorze danych
• Kategorie zbieranych danych
• Cele przetwarzania danych
• Podstawy prawne przetwarzania (RODO)
• Prawa użytkowników (dostęp, sprostowanie, usunięcie)
• Środki bezpieczeństwa i ochrony danych`
  },
  photo_consent: {
    title: 'Zgoda na publikację wizerunku dziecka',
    icon: Camera,
    storagePath: null, // No external document, inline content only
    description: 'Zgoda na publikowanie zdjęć dziecka w ramach platformy KConnect.',
    summary: `ZGODA NA PUBLIKACJĘ WIZERUNKU DZIECKA

1. ZAKRES ZGODY

Niniejsza zgoda dotyczy publikacji zdjęć i materiałów wideo z wizerunkiem mojego dziecka/dzieci w ramach platformy KConnect.

Zgoda obejmuje:
• Publikowanie zdjęć wykonanych podczas pobytu dziecka w placówce
• Udostępnianie zdjęć innym rodzicom i nauczycielom z tej samej grupy
• Przechowywanie zdjęć w systemie platformy

2. OGRANICZENIA

Zdjęcia z wizerunkiem dziecka:
• Będą widoczne TYLKO dla rodziców i nauczycieli z tej samej grupy przedszkolnej
• NIE będą publikowane w mediach społecznościowych
• NIE będą przekazywane osobom trzecim
• NIE będą wykorzystywane do celów marketingowych

3. PRAWA RODZICA

W każdej chwili mogę:
• Cofnąć niniejszą zgodę w ustawieniach aplikacji
• Zażądać usunięcia zdjęć mojego dziecka
• Ograniczyć widoczność zdjęć

Cofnięcie zgody nie wpływa na zgodność z prawem przetwarzania, którego dokonano przed jej cofnięciem.

[Wyrażenie tej zgody jest dobrowolne i nie wpływa na możliwość korzystania z pozostałych funkcji platformy]`
  }
};

// Role icons and labels
// Document flow varies by role:
// - Admin: Terms -> DPA -> Privacy (no photo consent)
// Teachers/parents: privacy only. Admin (branch_admin, super_admin): terms + DPA + privacy.
const ROLE_CONFIG = {
  super_admin: {
    icon: Building2,
    label: 'Administrator platformy',
    documents: ['terms', 'dpa', 'privacy']
  },
  branch_admin: {
    icon: Building2,
    label: 'Administrator placówki',
    documents: ['terms', 'dpa', 'privacy']
  },
  teacher: {
    icon: GraduationCap,
    label: 'Nauczyciel',
    documents: ['privacy']
  },
  parent: {
    icon: Users,
    label: 'Rodzic',
    documents: ['privacy', 'photo_consent']
  }
};

// Map document keys to consent_type values for database storage
const DOC_TO_CONSENT_TYPE = {
  terms: 'terms_of_service',
  privacy: 'privacy_policy',
  dpa: 'data_processing',
  photo_consent: 'photo_sharing_internal'
};

export default function Welcome() {
  const { userProfile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const grantConsents = useGrantMultipleConsents();

  const [currentStep, setCurrentStep] = useState(0); // 0 = welcome, 1+ = documents
  const [acceptedDocs, setAcceptedDocs] = useState({
    terms: false,
    privacy: false,
    dpa: false,
    photo_consent: false
  });
  const [saving, setSaving] = useState(false);
  const [documentUrls, setDocumentUrls] = useState({});
  const [loadingUrls, setLoadingUrls] = useState(true);

  // Fetch signed URLs for documents from storage
  useEffect(() => {
    const fetchDocumentUrls = async () => {
      setLoadingUrls(true);
      const urls = {};

      for (const [key, doc] of Object.entries(DOCUMENTS)) {
        if (doc.storagePath) {
          try {
            const { data, error } = await supabase.storage
              .from('documents')
              .createSignedUrl(doc.storagePath, 3600); // 1 hour expiry

            if (error) {
              logger.error(`Error fetching URL for ${key}:`, error);
            } else {
              urls[key] = data.signedUrl;
            }
          } catch (err) {
            logger.error(`Error fetching URL for ${key}:`, err);
          }
        }
      }

      setDocumentUrls(urls);
      setLoadingUrls(false);
    };

    fetchDocumentUrls();
  }, []);

  // Determine user role and required documents
  const userRole = userProfile?.active_role || userProfile?.primary_role || 'parent';
  const roleConfig = ROLE_CONFIG[userRole] || ROLE_CONFIG.parent;
  const requiredDocs = roleConfig.documents;
  const RoleIcon = roleConfig.icon;

  // Optional documents that can be skipped
  const OPTIONAL_DOCS = ['photo_consent'];

  // Check if all required docs are accepted (excluding optional ones)
  const allDocsAccepted = useMemo(() => {
    return requiredDocs
      .filter(docKey => !OPTIONAL_DOCS.includes(docKey))
      .every(docKey => acceptedDocs[docKey]);
  }, [acceptedDocs, requiredDocs]);

  // Handle document acceptance toggle
  const toggleDocAcceptance = (docKey) => {
    setAcceptedDocs(prev => ({
      ...prev,
      [docKey]: !prev[docKey]
    }));
  };

  // Save acceptance and proceed
  const handleComplete = async () => {
    if (!allDocsAccepted) {
      toast.error('Proszę zaakceptować wszystkie wymagane dokumenty');
      return;
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();

      // Store consents in the consents table
      const consentTypes = requiredDocs
        .filter(docKey => acceptedDocs[docKey])
        .map(docKey => DOC_TO_CONSENT_TYPE[docKey])
        .filter(Boolean);

      if (consentTypes.length > 0 && userProfile?.id) {
        await grantConsents.mutateAsync({
          userId: userProfile.id,
          consentTypes,
          userAgent: navigator.userAgent
        });
        // Refetch consents so Layout/RequiredConsentsDialog see fresh data before we navigate
        await queryClient.refetchQueries({ queryKey: ['consents'] });
      }

      // Also update metadata for backward compatibility
      const updates = {
        metadata: {
          ...userProfile?.metadata,
          terms_accepted_at: now,
          privacy_accepted_at: now,
          ...(requiredDocs.includes('dpa') && { dpa_accepted_at: now }),
          ...(requiredDocs.includes('photo_consent') && acceptedDocs.photo_consent && { photo_consent_accepted_at: now }),
          onboarding_completed: true
        }
      };

      await updateProfile(updates);
      toast.success('Dokumenty zostały zaakceptowane');

      // Navigate to home (tutorial overlay disabled until fully implemented)
      navigate('/');
    } catch (error) {
      logger.error('Error saving acceptance:', error);
      toast.error('Wystąpił błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  // Current document being displayed (if not on welcome step)
  const currentDoc = currentStep > 0 ? DOCUMENTS[requiredDocs[currentStep - 1]] : null;
  const currentDocKey = currentStep > 0 ? requiredDocs[currentStep - 1] : null;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      {/* Background image (same as login page) */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${background})` }}
      />
      <div className="absolute inset-0 bg-white/60" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl relative z-10"
      >
        <AnimatePresence mode="wait">
          {currentStep === 0 ? (
            // Welcome Step
            <motion.div
              key="welcome"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <Card className="shadow-xl border-0">
                <CardContent className="p-8 md:p-12">
                  {/* Logo */}
                  <div className="text-center mb-8">
                    <video
                      src={logo}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-24 h-24 object-contain mx-auto mb-6"
                    />
                    <h1 className="text-3xl font-bold text-slate-900 mb-2">
                      Witamy w KConnect!
                    </h1>
                    <p className="text-slate-600 text-lg">
                      Dziękujemy za skorzystanie z platformy
                    </p>
                  </div>

                  {/* User info */}
                  <div className="bg-slate-50 rounded-xl p-4 mb-8 flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                      <RoleIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {userProfile?.first_name} {userProfile?.last_name}
                      </p>
                      <p className="text-sm text-slate-500">{roleConfig.label}</p>
                    </div>
                  </div>

                  {/* Welcome message */}
                  <div className="space-y-4 mb-8">
                    <p className="text-slate-600">
                      Mamy nadzieję, że nasza platforma spełni Państwa oczekiwania i ułatwi
                      codzienną komunikację między placówką a rodzicami.
                    </p>
                    <p className="text-slate-600">
                      Aby rozpocząć korzystanie z platformy, prosimy o zapoznanie się
                      i zaakceptowanie poniższych dokumentów:
                    </p>
                  </div>

                  {/* Document list */}
                  <div className="space-y-3 mb-8">
                    {requiredDocs.map((docKey, index) => {
                      const doc = DOCUMENTS[docKey];
                      const DocIcon = doc.icon;
                      const isAccepted = acceptedDocs[docKey];
                      const isOptional = OPTIONAL_DOCS.includes(docKey);

                      return (
                        <div
                          key={docKey}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                            isAccepted
                              ? 'bg-green-50 border-green-200'
                              : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isAccepted ? 'bg-green-100' : 'bg-slate-100'
                          }`}>
                            {isAccepted ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <DocIcon className="w-5 h-5 text-slate-500" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{doc.title}</p>
                            {isOptional && (
                              <p className="text-xs text-slate-400">Opcjonalne</p>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            isAccepted
                              ? 'bg-green-100 text-green-700'
                              : isOptional
                                ? 'bg-blue-50 text-blue-600'
                                : 'bg-slate-100 text-slate-500'
                          }`}>
                            {isAccepted ? 'Zaakceptowano' : isOptional ? 'Opcjonalne' : 'Wymaga akceptacji'}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Action button */}
                  <Button
                    size="lg"
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    onClick={() => setCurrentStep(1)}
                  >
                    Przejdź do dokumentów
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            // Document Step
            <motion.div
              key={`doc-${currentStep}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card className="shadow-xl border-0">
                <CardContent className="p-6 md:p-8">
                  {/* Progress indicator */}
                  <div className="flex items-center gap-2 mb-6">
                    {requiredDocs.map((_, index) => (
                      <div
                        key={index}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          index < currentStep
                            ? 'bg-blue-600'
                            : index === currentStep - 1
                              ? 'bg-blue-400'
                              : 'bg-slate-200'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Document header */}
                  <div className="flex items-center gap-3 mb-4">
                    {currentDoc && (
                      <>
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <currentDoc.icon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-slate-900">{currentDoc.title}</h2>
                          <p className="text-sm text-slate-500">
                            Dokument {currentStep} z {requiredDocs.length}
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Document description */}
                  {currentDoc?.description && (
                    <p className="text-sm text-slate-600 mb-4">
                      {currentDoc.description}
                    </p>
                  )}

                  {/* Document content - tabs for documents with storage, just content for inline */}
                  {currentDoc?.storagePath ? (
                    <Tabs defaultValue="document" className="mb-6">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="document" className="flex items-center gap-2">
                          <DocIcon className="w-4 h-4" />
                          Pełny dokument
                        </TabsTrigger>
                        <TabsTrigger value="summary" className="flex items-center gap-2">
                          <List className="w-4 h-4" />
                          Podsumowanie
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="document" className="mt-4">
                        <div className="rounded-lg border bg-white overflow-hidden">
                          {documentUrls[currentDocKey] ? (
                            <DocxViewer url={documentUrls[currentDocKey]} height="320px" />
                          ) : (
                            <div className="flex items-center justify-center h-[320px] p-8">
                              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                            </div>
                          )}
                        </div>
                      </TabsContent>
                      <TabsContent value="summary" className="mt-4">
                        <ScrollArea className="h-[320px] rounded-lg border bg-slate-50 p-4">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                            Podsumowanie dokumentu:
                          </p>
                          <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">
                            {currentDoc?.summary}
                          </pre>
                        </ScrollArea>
                      </TabsContent>
                    </Tabs>
                  ) : (
                    <ScrollArea className="h-[280px] rounded-lg border bg-slate-50 p-4 mb-6">
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                          Treść:
                        </p>
                        <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">
                          {currentDoc?.summary}
                        </pre>
                      </div>
                    </ScrollArea>
                  )}

                  {/* Acceptance checkbox */}
                  {(() => {
                    const isOptional = OPTIONAL_DOCS.includes(currentDocKey);
                    return (
                      <label className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors mb-6 ${
                        isOptional ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' : 'bg-white hover:bg-slate-50'
                      }`}>
                        <Checkbox
                          checked={acceptedDocs[currentDocKey]}
                          onCheckedChange={() => toggleDocAcceptance(currentDocKey)}
                          className="mt-0.5"
                        />
                        <div>
                          <p className="font-medium text-slate-900">
                            {isOptional
                              ? 'Wyrażam zgodę na publikację wizerunku mojego dziecka/dzieci'
                              : 'Zapoznałem/am się z treścią dokumentu i akceptuję jego postanowienia'
                            }
                          </p>
                          <p className={`text-sm mt-1 ${isOptional ? 'text-blue-600' : 'text-slate-500'}`}>
                            {isOptional
                              ? 'Zgoda jest dobrowolna i można ją zmienić w ustawieniach'
                              : 'Akceptacja jest wymagana do korzystania z platformy'
                            }
                          </p>
                        </div>
                      </label>
                    );
                  })()}

                  {/* Navigation buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(prev => prev - 1)}
                      className="flex-1"
                    >
                      Wstecz
                    </Button>

                    {currentStep < requiredDocs.length ? (
                      <Button
                        onClick={() => setCurrentStep(prev => prev + 1)}
                        disabled={!OPTIONAL_DOCS.includes(currentDocKey) && !acceptedDocs[currentDocKey]}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                      >
                        {OPTIONAL_DOCS.includes(currentDocKey) && !acceptedDocs[currentDocKey] ? 'Pomiń' : 'Dalej'}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    ) : (
                      <Button
                        onClick={handleComplete}
                        disabled={!allDocsAccepted || saving}
                        className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Zapisywanie...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Zakończ i rozpocznij
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-6">
          W razie pytań prosimy o kontakt: kontakt@kconnect.pl
        </p>
      </motion.div>
    </div>
  );
}
